// pages/files.js — Files page.
//
// Owns the `data-page="files"` section. Derives a per-file activity
// summary from state.leasesAll (group by friendly file path, count
// active vs. recent, surface the most recent tool + member).
import { state } from "../state.js";
import { isTeamUnreachable, teamUnavailableHTML } from "../team-status.js";
import { $, patchHTML, loadingHTML } from "../dom.js";
import { escapeHTML, ago, parseUTC } from "../util.js";
import { friendlyResource, looksLikeHashedResource, parseAgentID } from "../format.js";
import { memberName, leaseMachine } from "../members.js";

// fileIconSVG returns the inline document icon used as the leading
// visual on every file row. Same size + alignment contract as
// worktreeIconSVG so the two pages share visual rhythm.
function fileIconSVG() {
  return (
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round">' +
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
    '<polyline points="14 2 14 8 20 8"/>' +
    '<line x1="9" y1="13" x2="15" y2="13"/>' +
    '<line x1="9" y1="17" x2="15" y2="17"/>' +
    "</svg>"
  );
}

// fileTitleAndDir splits "src/components/Foo.tsx" into "Foo.tsx" +
// "src/components/" so the row's primary text is the file name and
// the secondary text is its location. For paths with no slash (rare)
// the dir is empty.
function fileTitleAndDir(path) {
  const i = path.lastIndexOf("/");
  if (i < 0) return { name: path, dir: "" };
  return { name: path.slice(i + 1), dir: path.slice(0, i + 1) };
}

function renderFilesPage() {
  const wrap = $("files-list");
  if (!wrap) return;

  const oneHourAgo = Date.now() - 3600 * 1000;
  const byFile = new Map();
  // Track leases whose Resource is hashed and undecryptable separately
  // — previously they were dropped silently, leaving the page nearly
  // empty when state.hmacKey is missing. We surface them as a single
  // placeholder card linking to Machines → File Path Decryption so the user
  // knows where the rest of their files went.
  let encryptedCount = 0;
  for (const l of state.leasesAll || []) {
    const resource = l.Resource || "";
    const path = friendlyResource(resource);
    if (!path) {
      if (looksLikeHashedResource(resource)) encryptedCount++;
      continue;
    }
    let entry = byFile.get(path);
    if (!entry) {
      entry = {
        path,
        active: 0,
        recent: 0,
        lastSeen: 0,
        lastAgent: "",
        lastUser: "",
      };
      byFile.set(path, entry);
    }
    // parseUTC handles D1's space-separated timestamps (no Z) correctly;
    // raw `new Date()` would shift by local offset and break the
    // "recent count" window for users outside UTC.
    const acquiredMs = l.AcquiredAt ? parseUTC(l.AcquiredAt) : 0;
    if (l.State === "held") entry.active++;
    if (acquiredMs >= oneHourAgo) entry.recent++;
    if (acquiredMs > entry.lastSeen) {
      entry.lastSeen = acquiredMs;
      entry.lastAgent = parseAgentID(l.AgentID || "").tool || "";
      entry.lastUser = memberName(leaseMachine(l)) || "";
    }
  }

  if (byFile.size === 0 && encryptedCount === 0 && isTeamUnreachable()) {
    patchHTML(wrap, teamUnavailableHTML("Files being edited"));
    return;
  }

  if (byFile.size === 0 && encryptedCount === 0 && state.loading) {
    patchHTML(wrap, loadingHTML("files"));
    return;
  }
  if (byFile.size === 0 && encryptedCount === 0) {
    patchHTML(
      wrap,
      `<div class="empty">
        <div class="empty-title">File-level leasing active</div>
        <div class="empty-detail">Each file gets its own lease automatically. No configuration needed &mdash; files appear here as agents start editing them. Open the <strong>Leases</strong> tab for the live audit log.</div>
      </div>`,
    );
    return;
  }

  // Sort by most recent activity first.
  const files = Array.from(byFile.values()).sort(
    (a, b) => b.lastSeen - a.lastSeen,
  );

  // When some/all leases STILL came back hashed after decryption, prepend a
  // single placeholder card. Keeps the page honest about total counts. The
  // framing depends on whether a decrypt key is loaded — mirrors the
  // events-page clarity fix (format.js ~786):
  //   • NO key  → actionable: "N encrypted files" + "Import decrypt key" CTA.
  //   • key set → these went through the decrypt path and still didn't resolve.
  //     Two honest causes: (a) no path hint was stored (older / hintless
  //     events) → genuinely unrecoverable; (b) the key is STALE/rotated →
  //     re-import fixes it. The copy names both and keeps a link to Machines so
  //     the re-import case has a one-click path. (The root-cause "poller never
  //     decrypted recoverable paths" bug is fixed in overview.js; this card is
  //     now reserved for the truly-hintless / stale-key cases.)
  const haveKey = !!state.hmacKey;
  const encTitle = haveKey
    ? encryptedCount + ' file' + (encryptedCount === 1 ? '' : 's') + ' without a recorded path'
    : encryptedCount + ' encrypted file' + (encryptedCount === 1 ? '' : 's');
  const encChip = haveKey
    ? '<a href="#" data-page-link="machines" class="type-badge" style="text-decoration: none; color: var(--text-tertiary, var(--text-muted)); border-color: var(--border);">Re-import key</a>'
    : '<a href="#" data-page-link="machines" class="type-badge tb-acquired" style="text-decoration: none;">Import decrypt key</a>';
  const encSubtitle = haveKey
    ? 'These leases were synced without a path hint, so there\'s no filename to show. Your key is loaded and they\'re being coordinated normally. (If filenames you expect are missing, your team may have changed its team key — re-import it from Machines → File Path Decryption.)'
    : 'These files have leases on them, but their paths are scrambled with your team key. Import the key from Machines → File Path Decryption to see filenames.';
  const encBorder = haveKey ? 'var(--border)' : 'var(--warning, #f59e0b)';
  const encBg = haveKey ? 'transparent' : 'var(--warning-soft, rgba(245, 158, 11, 0.06))';
  const encIconColor = haveKey ? 'var(--text-tertiary, var(--text-muted))' : 'var(--warning, #f59e0b)';
  const encryptedCard = encryptedCount > 0
    ? '<div class="row-card" style="border-color: ' + encBorder + '; background: ' + encBg + ';">'
      + '<div class="row-card-icon" style="color: ' + encIconColor + ';">'
      + fileIconSVG()
      + '</div>'
      + '<div class="row-card-body">'
      + '<div class="row-card-titleline">'
      + '<strong class="row-card-title" style="font-size: 13px;">'
      + escapeHTML(encTitle)
      + '</strong>'
      + '<div class="row-card-chips">'
      + encChip
      + '</div>'
      + '</div>'
      + '<div class="row-card-subtitle">' + escapeHTML(encSubtitle) + '</div>'
      + '</div>'
      + '</div>'
    : '';

  patchHTML(
    wrap,
    encryptedCard +
    files
      .map((f) => {
        const ageStr = f.lastSeen
          ? ago(new Date(f.lastSeen).toISOString())
          : "";
        const split = fileTitleAndDir(f.path);
        // Active chip uses the same tb-acquired/tb-released treatment
        // as the Worktrees page. "0 active" gets the muted released
        // style so the eye lands on rows where work is happening.
        const activeChip =
          f.active > 0
            ? `<span class="type-badge tb-acquired">${f.active} active</span>`
            : `<span class="type-badge tb-released">idle</span>`;
        const recentChip = `<span class="type-badge tb-released">${f.recent} in last hour</span>`;
        // Meta strip: tool + actor, with middot separators only
        // between populated bits.
        const metaParts = [];
        if (f.lastAgent) {
          metaParts.push(
            'last edited by <strong style="color: var(--info);">' +
              escapeHTML(f.lastAgent) +
              "</strong>",
          );
        }
        if (f.lastUser) metaParts.push(escapeHTML(f.lastUser));
        const metaStrip = metaParts.join(
          '<span style="color: var(--text-tertiary);">&middot;</span>',
        );
        return (
          '<div class="row-card">' +
          '<div class="row-card-icon" style="color: var(--accent);">' +
          fileIconSVG() +
          "</div>" +
          '<div class="row-card-body">' +
          '<div class="row-card-titleline">' +
          '<strong class="row-card-title" style="font-family: var(--font-mono); font-size: 12.5px;">' +
          escapeHTML(split.name) +
          "</strong>" +
          '<div class="row-card-chips">' +
          activeChip +
          recentChip +
          "</div>" +
          "</div>" +
          (split.dir
            ? '<div class="row-card-subtitle">' +
              escapeHTML(split.dir) +
              "</div>"
            : "") +
          (metaStrip
            ? '<div class="row-card-meta">' + metaStrip + "</div>"
            : "") +
          "</div>" +
          '<div class="row-card-aside">' +
          '<span class="row-card-time">' +
          escapeHTML(ageStr) +
          "</span>" +
          "</div>" +
          "</div>"
        );
      })
      .join(""),
  );
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  renderFilesPage();
  tickHandler = () => {
    if (state.activePage === "files") renderFilesPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="files"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
